import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/language-library';

export default function LanguageLibrary() {
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [editedTexts, setEditedTexts] = useState({});
  const [pickedIndex, setPickedIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Load all clauses on mount
  useEffect(() => {
    fetch(`${API}/clauses`)
      .then(r => r.json())
      .then(data => {
        setTags(data.tags || []);
        if (data.tags && data.tags.length > 0) {
          setSelectedTag(data.tags[0].tag);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // When selectedTag changes, load its saved state
  useEffect(() => {
    const tagData = tags.find(t => t.tag === selectedTag);
    if (!tagData) return;
    setPickedIndex(tagData.selected_index ?? null);
    // Pre-fill edited texts with saved text or original
    const texts = {};
    tagData.variants.forEach((v, i) => {
      if (tagData.selected_index === i && tagData.selected_text) {
        texts[i] = tagData.selected_text;
      } else {
        texts[i] = v.text;
      }
    });
    setEditedTexts(texts);
    setSavedFlash(false);
  }, [selectedTag, tags]);

  const currentTag = tags.find(t => t.tag === selectedTag);

  const handleSave = useCallback(async () => {
    if (pickedIndex === null || !currentTag) return;
    setSaving(true);
    try {
      await fetch(`${API}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tag: selectedTag,
          selected_index: pickedIndex,
          selected_text: editedTexts[pickedIndex] || currentTag.variants[pickedIndex]?.text,
        }),
      });
      // Update local state
      setTags(prev => prev.map(t =>
        t.tag === selectedTag
          ? { ...t, selected_index: pickedIndex, selected_text: editedTexts[pickedIndex] }
          : t
      ));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      console.error('Save failed', e);
    }
    setSaving(false);
  }, [pickedIndex, selectedTag, editedTexts, currentTag]);

  const filteredTags = searchQuery
    ? tags.filter(t =>
        t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.tag.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tags;

  const gridCols = currentTag
    ? currentTag.variants.length <= 1 ? 1
    : currentTag.variants.length <= 3 ? 2
    : 3
    : 2;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading language library...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Left sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>Clause Tags</div>
        <input
          type="text"
          placeholder="Search tags..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <div style={styles.tagList}>
          {filteredTags.map(t => (
            <div
              key={t.tag}
              onClick={() => setSelectedTag(t.tag)}
              style={{
                ...styles.tagItem,
                ...(t.tag === selectedTag ? styles.tagItemActive : {}),
              }}
            >
              <span style={styles.tagDot(t.selected_index != null)}>●</span>
              <span style={styles.tagLabel}>{t.label}</span>
              <span style={styles.tagCount}>{t.variants.length}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main panel */}
      <div style={styles.main}>
        {currentTag ? (
          <>
            {/* Header */}
            <div style={styles.mainHeader}>
              <div>
                <h2 style={styles.tagTitle}>{currentTag.label}</h2>
                <span style={styles.tagSubtitle}>
                  {currentTag.variants.length} variant{currentTag.variants.length !== 1 ? 's' : ''} across source documents
                </span>
              </div>
              <div style={styles.headerActions}>
                {savedFlash && <span style={styles.savedBadge}>Saved</span>}
                <button
                  onClick={handleSave}
                  disabled={pickedIndex === null || saving}
                  style={{
                    ...styles.saveBtn,
                    ...(pickedIndex === null ? styles.saveBtnDisabled : {}),
                  }}
                >
                  {saving ? 'Saving...' : 'Save Selection'}
                </button>
              </div>
            </div>

            {/* Cards grid */}
            <div style={{ ...styles.grid, gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
              {currentTag.variants.map((v, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.card,
                    ...(pickedIndex === i ? styles.cardSelected : {}),
                  }}
                >
                  <div style={styles.cardHeader}>
                    <span style={styles.sourceName} title={v.source}>
                      {v.source.replace('Tax Certificate - ', '').replace('.docx', '')}
                    </span>
                    <span style={styles.dealBadge}>{v.deal_type}</span>
                  </div>
                  <div style={styles.sectionInfo}>
                    {v.section && <span>Section {v.section}</span>}
                    {v.heading && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{v.heading}</span>}
                  </div>
                  <textarea
                    value={editedTexts[i] ?? v.text}
                    onChange={e => setEditedTexts(prev => ({ ...prev, [i]: e.target.value }))}
                    style={styles.textArea}
                  />
                  <button
                    onClick={() => setPickedIndex(i)}
                    style={{
                      ...styles.selectBtn,
                      ...(pickedIndex === i ? styles.selectBtnActive : {}),
                    }}
                  >
                    {pickedIndex === i ? 'Selected' : 'Select This Version'}
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={styles.empty}>Select a clause tag from the sidebar</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 60px)',
    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    background: '#f5f0e8',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    fontSize: 16,
    color: '#666',
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    background: '#2c3e50',
    color: '#ecf0f1',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #1a252f',
  },
  sidebarHeader: {
    padding: '14px 16px 8px',
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: '#95a5a6',
  },
  searchInput: {
    margin: '0 10px 8px',
    padding: '6px 10px',
    borderRadius: 4,
    border: 'none',
    fontSize: 13,
    background: '#34495e',
    color: '#ecf0f1',
    outline: 'none',
  },
  tagList: {
    flex: 1,
    overflowY: 'auto',
  },
  tagItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: 13,
    borderLeft: '3px solid transparent',
    transition: 'background 0.15s',
  },
  tagItemActive: {
    background: '#34495e',
    borderLeftColor: '#3498db',
  },
  tagDot: (saved) => ({
    marginRight: 8,
    fontSize: 8,
    color: saved ? '#2ecc71' : '#555',
  }),
  tagLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tagCount: {
    marginLeft: 6,
    fontSize: 11,
    color: '#7f8c8d',
    background: '#1a252f',
    borderRadius: 8,
    padding: '1px 6px',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  mainHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px 12px',
    borderBottom: '1px solid #ddd',
    background: '#fff',
  },
  tagTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: '#2c3e50',
  },
  tagSubtitle: {
    fontSize: 13,
    color: '#7f8c8d',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  savedBadge: {
    background: '#2ecc71',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
  },
  saveBtn: {
    padding: '8px 20px',
    borderRadius: 4,
    border: 'none',
    background: '#3498db',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    background: '#bdc3c7',
    cursor: 'not-allowed',
  },
  grid: {
    display: 'grid',
    gap: 16,
    padding: 20,
    overflowY: 'auto',
    flex: 1,
  },
  card: {
    background: '#fff',
    border: '2px solid #e0e0e0',
    borderRadius: 6,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'border-color 0.2s',
  },
  cardSelected: {
    borderColor: '#2ecc71',
    boxShadow: '0 0 0 1px #2ecc71',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sourceName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2c3e50',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '70%',
  },
  dealBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 3,
    background: '#eaf2f8',
    color: '#2980b9',
  },
  sectionInfo: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  textArea: {
    flex: 1,
    minHeight: 180,
    padding: 10,
    border: '1px solid #ddd',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: "'Segoe UI', Arial, sans-serif",
    lineHeight: 1.5,
    resize: 'vertical',
    color: '#333',
  },
  selectBtn: {
    padding: '8px 0',
    borderRadius: 4,
    border: '1px solid #ddd',
    background: '#f8f9fa',
    color: '#555',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  selectBtnActive: {
    background: '#2ecc71',
    color: '#fff',
    border: '1px solid #2ecc71',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#999',
    fontSize: 16,
  },
};
